// PWA install-prompt state — holds the deferred `beforeinstallprompt` event so
// the custom install banner (.install-prompt in the old app) can trigger it on
// demand. vite-plugin-pwa handles SW registration/updates, but NOT this banner.
//
// Dismissal is persisted as a 1-day snooze in localStorage so the banner does
// not reappear on every reload/cold relaunch (Chromium re-fires
// `beforeinstallprompt` each load); after the snooze lapses it may show again.

import { create } from 'zustand'

// Not in lib.dom — the Chromium-only beforeinstallprompt event.
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export const SNOOZE_KEY = 'pwa_install_snooze_until'
const SNOOZE_MS = 24 * 60 * 60 * 1000

// True while a dismissal snooze is still active. Reads localStorage defensively
// so private-mode / disabled storage just degrades to "not snoozed".
export function isInstallSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY)
    const until = raw ? Number(raw) : 0
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

interface InstallState {
  deferredPrompt: BeforeInstallPromptEvent | null
  dismissed: boolean
  setPrompt: (e: BeforeInstallPromptEvent) => void
  dismiss: () => void
  clear: () => void
}

export const useInstallStore = create<InstallState>()((set) => ({
  deferredPrompt: null,
  dismissed: isInstallSnoozed(),

  setPrompt: (e) => set({ deferredPrompt: e }),
  dismiss: () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    } catch {
      /* storage unavailable — fall back to in-memory dismissal for the session */
    }
    set({ dismissed: true })
  },
  clear: () => set({ deferredPrompt: null }),
}))
